import { every } from 'lodash'
import { debounce } from 'throttle-debounce'
import * as React from 'react'
import { connect } from 'react-redux'

import * as actions from 'common/actions'
import { validationDataSelector } from 'embed/selectors'
import { ValidationData } from 'common/services/Validation'
import { Fields } from 'embed/constants/fields'
import {
  ConfigKeys,
  DepositProductTypesOptions,
  Filter,
  Filters,
  GoogleAnalyticsActions,
  Product,
} from 'even-ts-static'
import View from './index'

export interface Props {
  defaultDepositsProductType?: DepositProductTypesOptions
  dispatchFetchDepositsRateTables: () => void
  dispatchFetchZipcodeFromIP: () => void
  dispatchFilterChanged: (filter: Filter, value, userInitiated: boolean) => void
  dispatchFiltersChanged: (filters: Filters) => void
  dispatchTrackGoogleAnalyticsFilterChanged: (filter: Filter) => void
  filters: Filters
  resultsUuid?: string
  validationData: ValidationData
  viewportHeight: number
}

const Container: React.FC<Props> = ({
  defaultDepositsProductType,
  dispatchFetchDepositsRateTables,
  dispatchFetchZipcodeFromIP,
  dispatchFilterChanged,
  dispatchFiltersChanged,
  dispatchTrackGoogleAnalyticsFilterChanged,
  filters,
  resultsUuid,
  validationData,
  viewportHeight,
}) => {
  const debouncedFetch = debounce(500, true, (filter: Filter, value) => {
    const allFiltersValid = every(filters, (filterValue, key) => {
      const field = Object.values(Fields).find((el) => el.key === key)
      return key === filter
        ? field !== undefined && field.validate(value, validationData)
        : field === undefined || field.validate(filterValue, validationData)
    })
    if (allFiltersValid) {
      dispatchFetchDepositsRateTables()
    }
  })

  React.useEffect(() => {
    // Set default filters
    if (defaultDepositsProductType !== undefined) {
      dispatchFiltersChanged({
        [Filter.ProductSubType]: defaultDepositsProductType,
        ...filters,
      })
    }
    // Fetch Zipcode from IP if we haven't set it yet from a query param
    if (filters.zipcode === undefined) {
      dispatchFetchZipcodeFromIP()
    }
    // Load initial results (if a result uuid was not specified in the config)
    if (resultsUuid === undefined) {
      dispatchFetchDepositsRateTables()
    }
    return () => {
      debouncedFetch.cancel()
    }
  }, [])

  const onChange = React.useCallback(
    (filter: Filter, value, _isValid, userInitiated = true) => {
      // Only trigger GA event if the user actually changed the field (vs us dispatching onChange due to
      // showing bankrate vs direct offers in DepositsProductType filter)
      if (userInitiated) {
        dispatchTrackGoogleAnalyticsFilterChanged(filter)
      }
      dispatchFilterChanged(filter, value, userInitiated)
      // Dont fetch if the product subtype changes changes
      if (filter !== Filter.ProductSubType) {
        debouncedFetch(filter, value)
      }
    },
    [debouncedFetch, dispatchFilterChanged, dispatchTrackGoogleAnalyticsFilterChanged],
  )

  return (
    <View
      defaultDepositsProductType={defaultDepositsProductType}
      onChange={onChange}
      filters={filters}
      viewportHeight={viewportHeight}
    />
  )
}

const mapStateToProps = (state) => ({
  filters: state.filters[Product.Deposits] ?? {},
  resultsUuid: state.config[ConfigKeys.ResultsUuid],
  validationData: validationDataSelector(state),
  viewportHeight: state.ui.viewport.height,
})

const mapDispatchToProps = (dispatch) => ({
  dispatchFetchDepositsRateTables: () => dispatch(actions.fetchRateTables(Product.Deposits)),
  dispatchFetchZipcodeFromIP: () => dispatch(actions.fetchZipcodeFromIP(Product.Deposits)),
  dispatchFilterChanged: (filter, value, userInitiated) =>
    dispatch(actions.filterChanged(Product.Deposits, filter, value, { userInitiated })),
  dispatchFiltersChanged: (filters) => dispatch(actions.filtersChanged(Product.Deposits, filters)),
  dispatchTrackGoogleAnalyticsFilterChanged: (filter: Filter) =>
    dispatch(actions.trackGoogleAnalyticsEvent(GoogleAnalyticsActions.Filtered, undefined, undefined, filter)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Container)
